<template>
    <div class="feedback-check-wrap">
        <!-- TODO: 평가 텍스트 변경 필요 -->
        <p class="title h5">{{ props.userFeedback.Title }}</p>
        <p class="desc caption--regular">{{ props.userFeedback.Body }}</p>
        <div class="feedback-buttons">
            <button
                class="feedback-button good"
                :class="{
                    active: selectedFeedback === 'good',
                }"
                @click="handleSelectFeedback('good')"
            >
                <img
                    src="../../../public/assets/images/ic_good_color.png"
                    :alt="t('trend_report_feedback_check_good_alt')"
                    v-if="selectedFeedback === 'good'"
                />
                <img
                    src="../../../public/assets/images/ic_good.png"
                    :alt="t('trend_report_feedback_check_good_alt')"
                    v-else
                />
            </button>
            <button
                class="feedback-button bad"
                :class="{
                    active: selectedFeedback === 'bad',
                }"
                @click="handleSelectFeedback('bad')"
            >
                <img
                    src="../../../public/assets/images/ic_good_color.png"
                    :alt="t('trend_report_feedback_check_bad_alt')"
                    v-if="selectedFeedback === 'bad'"
                />
                <img
                    src="../../../public/assets/images/ic_good.png"
                    :alt="t('trend_report_feedback_check_bad_alt')"
                    v-else
                />
            </button>
        </div>
        <div class="free-feedback-wrap" ref="freeFeedbackWrapRef">
            <textarea
                name="feedback-textarea"
                id="feedback-textarea"
                ref="feedbackTextareaRef"
                :value="feedbackTextarea"
                @input="handleTextareaInput"
                :placeholder="`이번 변화 리포트에 대한 자유로운 의견을 제출해주세요.\n다음 리포트에 반영될 수 있습니다.`"
                @focus="handleFocusTextarea"
                @blur="handleBlurTextarea"
            ></textarea>
            <!-- 텍스트 영역 포커싱 시에만 하단에 노출 (grid-template-rows로 실제 높이만큼 부드럽게 접힘) -->
            <Transition name="slide-feedback-button" @after-enter="applyAndroidKeyboardSpace">
                <div class="feedback-button-collapse" v-if="isFocusedTextarea">
                    <div class="feedback-button-clip">
                        <div class="feedback-button-wrap">
                            <button class="feedback-button h6" @mousedown.prevent @click="handleCancelFreeFeedback">
                                {{ t('button_close') }}
                            </button>
                            <button
                                class="feedback-button h6"
                                @mousedown.prevent
                                @click="handleSubmitFreeFeedback"
                                :disabled="computedIsFreeFeedbackSubmitDisabled"
                            >
                                {{ t('button_submit') }}
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
        <!-- 데이터 넘기기 -->
        <Transition name="fade">
            <TrendReportFeedbackModal
                :userFeedbackQuestionData="userFeedbackQuestionData"
                v-if="isShowFeedbackModal && userFeedbackQuestionData"
                @closeModal="handleCloseModal"
                @submitFeedback="handleSubmitFeedback"
            />
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import TrendReportFeedbackModal from './TrendReportFeedbackModal.vue';
import { useTrendReportStore } from '@common/trend-report-common';
import { useToastStore } from '@common/common/stores/toast';
import type { TrendReportFeedbackQuestionDto, UserFeedback } from '@common/openapi-client/openapi';
import type { RequestFeedbackData } from '@common/trend-report-common';
import { isAppleDevice, hideKeyboard } from '@common/common/utils/utils';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const proReportStore = useTrendReportStore();
const toastStore = useToastStore();

const props = defineProps<{
    userFeedback: UserFeedback;
}>();

const emit = defineEmits(['updateUserFeedbackData']);

const isShowFeedbackModal = ref(false);
const selectedFeedback = ref<'good' | 'bad' | string>('');
const feedbackTextarea = ref('');
const userFeedbackQuestionData = ref<TrendReportFeedbackQuestionDto | null>(null);

// 자유 의견 텍스트 영역 포커싱 상태
const feedbackTextareaRef = ref<HTMLTextAreaElement | null>(null);
const freeFeedbackWrapRef = ref<HTMLElement | null>(null);
const isFocusedTextarea = ref(false);

const computedIsFreeFeedbackSubmitDisabled = computed(() => feedbackTextarea.value.trim().length === 0);

// 안드로이드 IME 조합 중에도 즉시 반영되도록 v-model 대신 input 이벤트로 직접 갱신 (제출 버튼 활성/비활성 즉시 반영)
const handleTextareaInput = (e: Event) => {
    feedbackTextarea.value = (e.target as HTMLTextAreaElement).value;
};

// --- 안드로이드 키보드 대응 ---
// 인앱 웹뷰는 키보드가 떠도 뷰포트 높이가 변하지 않아 키보드 높이를 감지할 수 없으므로(피드백 모달과 동일),
// 화면 높이의 약 1/3로 추정한다. 포커싱 시 페이지 영역(.pro-report-container) 하단 패딩을 그만큼 늘려
// 스크롤 여유를 만든 뒤, 버튼 영역이 키보드 바로 위에 붙도록 스크롤한다. (iOS는 네이티브가 처리하므로 제외)
let paddingTarget: HTMLElement | null = null;
let scrollContainer: HTMLElement | null = null;
let originalPaddingBottom = '';
let savedScrollTop = 0;

const getScrollParent = (el: HTMLElement | null): HTMLElement => {
    let node = el?.parentElement ?? null;
    while (node) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
    }
    return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
};

// 키보드 높이: visualViewport로 실제 감지되면 사용, 아니면(정적 높이 인앱 웹뷰) 화면 1/3로 추정
const getAndroidKeyboardHeight = () => {
    const detected = window.visualViewport ? window.innerHeight - window.visualViewport.height : 0;
    return detected > 50 ? detected : window.innerHeight / 3 + 10;
};

const applyAndroidKeyboardSpace = () => {
    if (isAppleDevice() || !isFocusedTextarea.value) return;

    const wrap = freeFeedbackWrapRef.value;
    if (!wrap) return;

    // 하단 패딩을 늘릴 대상(.pro-report-container)과 스크롤 컨테이너 캐싱
    if (!paddingTarget) {
        paddingTarget = wrap.closest<HTMLElement>('.pro-report-container');
        if (paddingTarget) originalPaddingBottom = paddingTarget.style.paddingBottom;
    }
    if (!scrollContainer) {
        scrollContainer = getScrollParent(wrap);
        savedScrollTop = scrollContainer.scrollTop; // 포커싱 전 스크롤 위치 저장 (해제 시 복원용)
    }
    if (!paddingTarget || !scrollContainer) return;

    const keyboard = getAndroidKeyboardHeight();

    // 1) 페이지 하단 영역을 키보드 높이만큼 늘려 스크롤 여유 공간 확보
    paddingTarget.style.paddingBottom = `${keyboard}px`;

    // 늘어난 높이가 스크롤 계산에 반영되도록 강제 리플로우 (영역 확장 → 스크롤 순서 보장)
    scrollContainer.getBoundingClientRect();

    // 2) 페이지를 스크롤해 버튼 영역 하단이 키보드(추정) 상단 바로 위에 붙도록
    const keyboardTop = window.innerHeight - keyboard;
    const gap = 8; // 버튼과 키보드 사이 여백(px)
    const overflowBelow = wrap.getBoundingClientRect().bottom - (keyboardTop - gap);
    if (overflowBelow > 0) {
        scrollContainer.scrollTop += overflowBelow;
    }
};

const resetAndroidKeyboardSpace = () => {
    // 패딩 제거 전에 스크롤을 원위치로 되돌려, 높이 축소에 따른 스크롤 클램프 점프 방지
    if (scrollContainer) {
        scrollContainer.scrollTop = savedScrollTop;
    }
    if (paddingTarget) {
        paddingTarget.style.paddingBottom = originalPaddingBottom;
    }
    paddingTarget = null;
    scrollContainer = null;
    originalPaddingBottom = '';
    savedScrollTop = 0;
};

const handleVisualViewportResize = () => {
    // 뷰포트가 실제로 줄어드는 웹뷰에서만 유효 (정적 높이 웹뷰는 focus/@after-enter로 처리)
    if (isFocusedTextarea.value) applyAndroidKeyboardSpace();
};

const handleFocusTextarea = () => {
    isFocusedTextarea.value = true;
    // 버튼 렌더 후 계산되도록 지연 실행 (버튼 펼침 완료 시 @after-enter에서도 재실행)
    requestAnimationFrame(() => requestAnimationFrame(applyAndroidKeyboardSpace));
};

const handleBlurTextarea = () => {
    // 하단 버튼은 mousedown.prevent로 포커싱을 유지하므로, 그 외 영역 클릭 시 자연스러운 해제를 위해 50ms 뒤 실행
    setTimeout(() => {
        // 1) 패딩(+스크롤) 먼저 원복 → 2) 다음 프레임에 버튼 영역 숨김 (한꺼번에 사라져 스크롤이 튀는 것 방지)
        resetAndroidKeyboardSpace();
        requestAnimationFrame(() => {
            isFocusedTextarea.value = false;
        });
    }, 50);
};

// 포커싱 해제 + 키보드 닫기 (blur → handleBlurTextarea에서 '패딩 원복 → 버튼 숨김' 순서로 처리)
const closeFreeFeedback = () => {
    feedbackTextareaRef.value?.blur();
    hideKeyboard();
};

onMounted(() => {
    if (isAppleDevice() || !window.visualViewport) return;
    window.visualViewport.addEventListener('resize', handleVisualViewportResize);
});

onUnmounted(() => {
    window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
    resetAndroidKeyboardSpace();
});

const handleCancelFreeFeedback = () => {
    closeFreeFeedback();
};

const handleSubmitFreeFeedback = () => {
    const subjectiveAnswerValue = feedbackTextarea.value.trim();
    if (!subjectiveAnswerValue) return;

    closeFreeFeedback();

    proReportStore
        .postSubjectiveFeedback(subjectiveAnswerValue)
        .then(resp => {
            if (resp && resp.IsSuccess) {
                feedbackTextarea.value = '';
                toastStore.setToast({
                    toastType: 'NORMAL',
                    title: t('trend_report_feedback_submit_toast'),
                    textColor: '#21242E',
                });
            } else {
                toastStore.setToast({
                    toastType: 'NORMAL',
                    title: t('trend_report_feedback_submit_error_toast'),
                    textColor: '#21242E',
                });
            }
        })
        .catch(() => {
            toastStore.setToast({
                toastType: 'NORMAL',
                title: t('trend_report_feedback_submit_error_toast'),
                textColor: '#21242E',
            });
        });
};

const setFeedbackValue = () => {
    if (props.userFeedback.IsPositive !== null) {
        selectedFeedback.value = props.userFeedback.IsPositive ? 'good' : 'bad';
    } else {
        selectedFeedback.value = '';
    }
};

const handleSelectFeedback = async (feedback: 'good' | 'bad') => {
    selectedFeedback.value = feedback;
    const isPositive = feedback === 'good' ? true : false;

    proReportStore.getFeedbackQuestion(isPositive).then(resp => {
        if (resp && resp.IsSuccess) {
            userFeedbackQuestionData.value = resp.Data;

            isShowFeedbackModal.value = true;
        }
    });
};

const handleCloseModal = () => {
    setFeedbackValue();
    isShowFeedbackModal.value = false;
};

const handleSubmitFeedback = (answer: RequestFeedbackData) => {
    let tempAnswer = answer;
    tempAnswer.SubjectiveChoice.SubjectiveAnswerValue =
        tempAnswer.SubjectiveChoice.SubjectiveAnswerValue === ''
            ? null
            : tempAnswer.SubjectiveChoice.SubjectiveAnswerValue;

    proReportStore.postFeedbackAnswer(tempAnswer).then(resp => {
        if (resp && resp.IsSuccess) {
            emit('updateUserFeedbackData', resp.Data);
            isShowFeedbackModal.value = false;
        } else {
            setFeedbackValue();
        }
    });
};

setFeedbackValue();
</script>

<style scoped lang="scss">
.feedback-check-wrap {
    display: flex;
    align-items: center;
    flex-direction: column;
    background-color: $grey-1;
    padding: 2rem;
    margin: 0 1.5rem 3rem 1.5rem;
    border-radius: 2rem;
    text-align: center;
    color: $grey-7;

    .desc {
        width: 80%;
        margin: 1rem 0;
        white-space: pre-line;
    }

    textarea {
        width: 100%;
        height: 7.6rem;
        margin-top: 1rem;
        box-sizing: border-box;
        border-radius: 1rem;
        border: 1px solid $grey-4;
        padding: 1rem;
        resize: none;
        font-size: 1.2rem;
        line-height: 150%;
        color: $grey-7;
        white-space: pre-line;

        &::placeholder {
            color: $grey-5;
            font-size: 1.2rem;
            line-height: 150%;
            font-weight: bold;
        }
    }
}

.feedback-buttons {
    display: flex;
    align-items: center;

    .feedback-button {
        width: 4.6rem;
        height: 3.3rem;
        border-radius: 1rem;
        background-color: $grey-3;

        & + .feedback-button {
            margin-left: 0.5rem;
        }

        img {
            width: 1.6rem;
            height: 1.6rem;
        }

        &.active {
            background-color: #333a4a;
        }

        &.bad {
            img {
                transform: rotate(180deg);
            }
        }
    }
}

.free-feedback-wrap {
    width: 100%;
}

// grid-template-rows 0fr↔1fr 애니메이션을 위한 래퍼 (실제 콘텐츠 높이만큼 부드럽게 접힘)
.feedback-button-collapse {
    display: grid;
    grid-template-rows: 1fr;
}

// 패딩 없는 클리핑 전용 요소 (패딩이 남는 잔상 없이 0까지 접히도록 분리)
.feedback-button-clip {
    overflow: hidden;
    min-height: 0;
}

.feedback-button-wrap {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 1rem 0.5rem 0;
    box-sizing: border-box;

    .feedback-button {
        flex: 1;
        box-sizing: border-box;
        height: 5rem;
        border-radius: 1.5rem;

        &:first-child {
            background-color: $primary-white;
            border: 1px solid $grey-4;
            color: $primary-black;
        }

        &:last-child {
            background-color: $grey-8;
            color: $pro-secondary-blue;

            &:disabled {
                background-color: $grey-7;
                color: $primary-white;
            }
        }
    }
}

// 하단 버튼 자연스럽게 접힘/펼침 (grid-template-rows 0fr↔1fr + opacity)
.slide-feedback-button-enter-from,
.slide-feedback-button-leave-to {
    grid-template-rows: 0fr;
    opacity: 0;
}

.slide-feedback-button-enter-active,
.slide-feedback-button-leave-active {
    transition:
        grid-template-rows 0.28s ease,
        opacity 0.28s ease;
}

.slide-feedback-button-enter-to,
.slide-feedback-button-leave-from {
    grid-template-rows: 1fr;
    opacity: 1;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
